import { Download, Send, Receipt, FileSpreadsheet } from "lucide-react";

const inputCls = "w-full border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-700 bg-white";

export function PeriodControls({ from, to, onFrom, onTo, onLoad, onDownload, onAuditDownload, onSend, loading, hasReport }) {
  return (
    <div className="border border-stone-200 bg-white p-6 mb-6">
      <div className="grid md:grid-cols-3 gap-4 mb-4">
        <Field label="Dal"><input type="date" value={from} onChange={onFrom} className={inputCls} data-testid="tax-from" /></Field>
        <Field label="Al"><input type="date" value={to} onChange={onTo} className={inputCls} data-testid="tax-to" /></Field>
        <div className="flex items-end">
          <button onClick={onLoad} disabled={loading} data-testid="tax-load"
            className="w-full bg-stone-900 text-stone-50 px-4 py-2.5 text-sm rounded-sm hover:bg-stone-800 disabled:opacity-60">
            <Receipt className="inline w-4 h-4 mr-2" />Calcola report
          </button>
        </div>
      </div>
      {hasReport && (
        <div className="flex flex-wrap gap-3 pt-2 border-t border-stone-200">
          <button onClick={onDownload} data-testid="tax-download"
            className="inline-flex items-center gap-2 border border-stone-300 px-4 py-2 text-sm hover:bg-stone-50">
            <Download className="w-4 h-4" /> Scarica CSV
          </button>
          <button onClick={onAuditDownload} data-testid="tax-audit-download"
            className="inline-flex items-center gap-2 border border-stone-300 px-4 py-2 text-sm hover:bg-stone-50"
            title="CSV per-occupante con esenzioni e motivazioni — utile per ispezioni fiscali">
            <FileSpreadsheet className="w-4 h-4" /> Audit per-occupante
          </button>
          <button onClick={onSend} data-testid="tax-send"
            className="inline-flex items-center gap-2 bg-blue-700 text-stone-50 px-4 py-2 text-sm rounded-sm hover:bg-blue-700">
            <Send className="w-4 h-4" /> Invia al Comune (mock)
          </button>
        </div>
      )}
    </div>
  );
}

export function TaxTotals({ totals }) {
  return (
    <div className="grid md:grid-cols-4 gap-4 mb-6">
      <KPI label="Prenotazioni" value={totals.bookings} />
      <KPI label="Ospiti" value={totals.guests} />
      <KPI label="Notti" value={totals.nights} />
      <KPI label="Importo" value={`€ ${totals.amount?.toLocaleString("it-IT", { minimumFractionDigits: 2 })}`} accent />
    </div>
  );
}

export function TaxTable({ rows }) {
  return (
    <div className="border border-stone-200 bg-white overflow-x-auto">
      <table className="w-full text-sm" data-testid="tax-table">
        <thead className="bg-stone-50 text-xs uppercase tracking-wider text-stone-500">
          <tr>
            <th className="text-left p-3">Ospite</th>
            <th className="text-left p-3">Check-in</th>
            <th className="text-left p-3">Check-out</th>
            <th className="text-right p-3">Notti</th>
            <th className="text-right p-3">Tassabili</th>
            <th className="text-right p-3">Pers.</th>
            <th className="text-right p-3">Importo</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={7} className="p-8 text-center text-stone-500">Nessuna prenotazione nel periodo.</td></tr>
          ) : rows.map(r => (
            <tr key={r.booking_id} className="border-t border-stone-200 hover:bg-stone-50">
              <td className="p-3 font-medium">{r.guest_name}</td>
              <td className="p-3 text-stone-600">{r.check_in}</td>
              <td className="p-3 text-stone-600">{r.check_out}</td>
              <td className="p-3 text-right">{r.nights}</td>
              <td className="p-3 text-right">{r.taxable_nights}</td>
              <td className="p-3 text-right">{r.num_guests}</td>
              <td className="p-3 text-right font-medium">€ {r.amount.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SettingsForm({ settings, setSettings, onSubmit }) {
  return (
    <form onSubmit={onSubmit} className="border border-stone-200 bg-white p-6 mb-6 grid md:grid-cols-4 gap-3" data-testid="tax-settings-form">
      <Field label="Comune"><input required value={settings.comune} onChange={(e) => setSettings(s => ({ ...s, comune: e.target.value }))} className={inputCls} data-testid="settings-comune" /></Field>
      <Field label="Provincia"><input maxLength={2} value={settings.provincia} onChange={(e) => setSettings(s => ({ ...s, provincia: e.target.value.toUpperCase() }))} className={`${inputCls} uppercase`} /></Field>
      <Field label="€ / notte"><input type="number" step="0.01" min={0} value={settings.tourist_tax_rate} onChange={(e) => setSettings(s => ({ ...s, tourist_tax_rate: e.target.value }))} className={inputCls} data-testid="settings-rate" /></Field>
      <Field label="Max notti"><input type="number" min={1} value={settings.tourist_tax_max_nights} onChange={(e) => setSettings(s => ({ ...s, tourist_tax_max_nights: e.target.value }))} className={inputCls} /></Field>
      <div className="md:col-span-4">
        <button data-testid="save-tax-settings" className="bg-stone-900 text-stone-50 px-5 py-2.5 text-sm rounded-sm hover:bg-stone-800">Salva</button>
      </div>
    </form>
  );
}

function Field({ label, children }) {
  return (<label className="block"><span className="text-xs text-stone-500">{label}</span><div className="mt-1">{children}</div></label>);
}

function KPI({ label, value, accent }) {
  return (
    <div className={`border p-5 ${accent ? "bg-blue-50 border-blue-200" : "bg-white border-stone-200"}`}>
      <div className="text-xs tracked text-stone-500">{label.toUpperCase()}</div>
      <div className="font-serif text-3xl tracking-tight mt-2">{value}</div>
    </div>
  );
}
